import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppUtil from "utilities/AppUtil";
import httpRequest from "../../services/httpRequest";
import snakecaseKeys from "snakecase-keys";

// Main URL Endpoint
const _urlPrefix = "/complaints";

export const fetchPageComplaint = createAsyncThunk(
  "complaint/fetchPageComplaint",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchComplaintDetail = createAsyncThunk(
  "complaint/fetchComplaintDetail",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const createComplaint = createAsyncThunk(
  "complaint/createComplaint",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.create.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const createPublicComplaint = createAsyncThunk(
  "complaint/createPublicComplaint",
  async (params) => {
    const result = await httpRequest({
      url: `/public/complaints`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.create.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateComplaintStatus = createAsyncThunk(
  "complaint/updateComplaintStatus",
  async (params) => {
    const { id, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/status`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const fetchComplaintEvidence = createAsyncThunk(
  "complaint/fetchComplaintEvidence",
  async (params) => {
    const { id } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const uploadComplaintEvidence = createAsyncThunk(
  "complaint/uploadComplaintEvidence",
  async (params) => {
    const { id, files, description } = params;
    const data = new FormData();

    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        data.append("files", files[i]);
      }
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys({ description }))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.add.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const updateComplaintEvidence = createAsyncThunk(
  "complaint/updateComplaintEvidence",
  async (params) => {
    const { id, evidenceId, ...requestData } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "PUT",
      data: snakecaseKeys(requestData),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

export const deleteComplaintEvidence = createAsyncThunk(
  "complaint/deleteComplaintEvidence",
  async (params) => {
    const { id, evidenceId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${id}/evidence/${evidenceId}`,
      method: "DELETE",
      successMessage: "toast.delete.200",
    });
    if (!result) {
      return;
    }
    return { ...result.data, evidenceId };
  }
);

const initialState = {
  list: [],
  totalElements: 0,
  totalPages: 0,
  pageNumber: 1,
  detail: {},
  evidence: [],
  stats: {
    overdue: 0,
    open: 0,
    underReview: 0,
    resolved: 0,
  },
};

const complaint = createSlice({
  name: "complaint",
  initialState: initialState,
  reducers: {},
  extraReducers: {
    [fetchPageComplaint.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.list = payload.content || payload;
        state.totalElements = payload.totalElements || 0;
        state.totalPages = payload.totalPages || 0;
        state.pageNumber = payload.pageNumber || 1;
        if (payload.stats) {
          state.stats = payload.stats;
        }
      }
    },
    [fetchComplaintDetail.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [updateComplaintStatus.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.detail = payload;
      }
    },
    [fetchComplaintEvidence.fulfilled]: (state, { payload }) => {
      state.evidence = payload || [];
    },
    [uploadComplaintEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.evidence.push(...(Array.isArray(payload) ? payload : [payload]));
      }
    },
    [updateComplaintEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        const index = state.evidence.findIndex(e => e.id === payload.id);
        if (index !== -1) {
          state.evidence[index] = payload;
        }
      }
    },
    [deleteComplaintEvidence.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.evidence = state.evidence.filter(
          e => e.id !== payload.evidenceId
        );
      }
    },
  },
});

const { reducer } = complaint;
export default reducer;

export const getComplaintList = createSelector(
  (state) => state.complaint.list,
  (list) => list
);

export const getComplaintPaging = createSelector(
  (state) => state.complaint,
  ({ totalElements, totalPages, pageNumber }) => ({ totalElements, totalPages, pageNumber })
);

export const getComplaintDetail = createSelector(
  (state) => state.complaint.detail,
  (detail) => detail
);

export const getComplaintEvidenceList = createSelector(
  (state) => state.complaint.evidence,
  (evidence) => evidence
);

export const getComplaintStats = createSelector(
  (state) => state.complaint.stats,
  (stats) => stats
);
